import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import infoSvg from "@assets/img/icons/info.svg";
import { Layout as AdminLayout } from "@components/admin/Layout";
import { SectionTitle } from "@components/admin/SectionTitle";
import { Alert } from "@components/Alert";
import { Button } from "@components/Button";
import { LogForm, RadioGroupInput } from "@components/Form";
import { CheckboxInput } from "@components/Forms/CheckboxInput";
import { FormAlert } from "@components/Forms/FormAlert";
import { FormStepSection } from "@components/Forms/FormStepSection";
import { PromptLeave } from "@components/Forms/PromptLeave";
import { SubmittingOverlay } from "@components/Forms/SubmittingOverlay";
import { Layout } from "@components/Layout";
import { QueryContainer } from "@components/QueryContainer";
import { Stepper } from "@components/Stepper";
import {
  BookingCreateContext,
  useBookingCreateContext,
} from "@components/utilities/BookingInquiry/BookingCreateContext";
import { BookingInquiryDisplay } from "@components/utilities/BookingInquiry/BookingInquiryDisplay";
import { BookingInquirySpecialNote } from "@components/utilities/BookingInquiry/BookingInquirySpecialNote";
import { CargosInput } from "@components/utilities/BookingInquiry/CargosInput";
import { CautionsDialog } from "@components/utilities/BookingInquiry/CautionsDialog";
import { ConsigneeInfoInput } from "@components/utilities/BookingInquiry/ConsigneeInfoInput";
import { Container } from "@components/utilities/BookingInquiry/Container";
import { CustomerInfoInput } from "@components/utilities/BookingInquiry/CustomerInfoInput";
import { PickupInfoInput } from "@components/utilities/BookingInquiry/PickupInfoInput";
import { PreferredArrivalDateInput } from "@components/utilities/BookingInquiry/PreferredArrivalDateInput";
import { RcepUsageInput } from "@components/utilities/BookingInquiry/RcepUsageInput";
import { ReceiverInfoInput } from "@components/utilities/BookingInquiry/ReceiverInfoInput";
import { RoutePlanFilter } from "@components/utilities/BookingInquiry/RoutePlanFilter";
import { RoutePlanList } from "@components/utilities/BookingInquiry/RoutePlanList";
import { ShipperInfoInput } from "@components/utilities/BookingInquiry/ShipperInfoInput";
import { TradeConditionSelector } from "@components/utilities/BookingInquiry/TradeConditionSelector";
import { VerticalSpace } from "@components/VerticalSpace";
import {
  bookingInquiryStrings,
  inquiryTitleStrings,
  // sampleFormHasPickupsWrongIds,
  // sampleFormNoPickupsQa,
  // sampleFormHasPickupsHandInput,
  // sampleFormNoPickups,
  steps,
} from "@data/bookingInquiry";
import { receivingCityOptions } from "@data/cities";
import { tradingAgencyOptions } from "@data/tradingAgency";
import { useBookingInquiryCreate } from "@hooks/mutation/useBookingInquiryCreate";
import { usePostBookingInquiryDraft } from "@hooks/mutation/usePostBookingInquiryDraft";
import {
  ResolvePostBookingInquiryResult,
  useResolvePostBookingInquiry,
} from "@hooks/mutation/useResolvePostBookingInquiry";
import { useBookingInquiryDraft } from "@hooks/queries/useBookingInquiryDraft";
import { useRoutePlanSimulator } from "@hooks/queries/useRoutePlanSimulator";
import { useUser } from "@hooks/queries/useUser";
import { useSearch } from "@hooks/useSearch";
import {
  BookingInquiryForm,
  bookingInquiryForm,
  bookingInquiryInitialValues,
} from "@models/BookingInquiry";
import {
  makeValidateBookingInquiryForm,
  mapBookingInquiryDraftToForm,
} from "@models/BookingInquiryUtils";
import { Country, countryStrings } from "@models/Country";
import { UseTradingAgency } from "@models/TradingAgency";
import { makeMuiStyles, useCommonStyles, useIsMobile } from "@styles";
import { FormError } from "@utils/errors";
import { formStrings, joinKeyPath, useErrorMessage } from "@utils/forms";
import { createStrings } from "@utils/local";
import { url } from "@utils/pages";
import { useShallowMemo } from "@utils/useShallowMemo";
import clsx from "clsx";
import { Formik, Form, FormikProps, useField } from "formik";
import { isLeft } from "fp-ts/lib/Either";
import * as t from "io-ts";
import { useHistory } from "react-router-dom";
import * as h from "tyrann-io";
import { unwrap } from "tyrann-io";

const strings = createStrings({
  ja: {
    title: "予約申込",
    dashboardTitle: "予約作成",
    shipperInfoTitle: "SHIPPER（輸出者）情報",
    consigneeInfoTitle: "CNEE（輸入者）情報",
    receiverInfoTitle: "納品先情報",
    deliveryDateTitle: "納品希望日",
    cargoInfoTitle: "集荷オプション、集荷先と荷物の内容",
    cargoPickupTitle: "集荷先と荷物の内容",
    cargoPortTitle: "荷受倉庫と荷物の内容",
    pickupOptions: "集荷オプション",
    miscInfoTitle: "貿易条件、お客様の管理番号",
    additionalFilesTitle: "添付資料",
    tradeConditionInfoTitle: "貿易条件",
    customerNoSpecialNoteTitle: "お客様の管理番号",
    tradeConditionsPrompt: "貿易条件（インコタームズ）",
    selectFile: "ファイルを選択",
    selectRoutePlan: `${inquiryTitleStrings.selectRoutePlan}へ`,
    edit: "修正する",
    confirmedCautions: (onClick: () => void) => (
      <b>
        <a onClick={onClick}>
          {" "}
          <b>注意事項</b>
        </a>
        を確認し、同意しました。ご予約を送信する前に必ず注意事項を読んでください
      </b>
    ),
    specialNotesHint: () => (
      <>
        ＊特記事項は特にないの場合は、特記事項欄に「
        <a>なし</a>
        」をご記載ください。
        <br />
      </>
    ),
    thisFieldIsRequired: "この欄は必須です",
    fixError:
      "正しく入力されていない項目があります。エラーメッセージをご確認の上、もう一度ご入力ください。",
    useTradingAgencyQuestion: "中国の有料貿易代行サービスはご利用されますか？",
    useTradingAgencyHint:
      "「利用する」または「わからない」場合は、事前に左下の「...」アイコンをクリックして、D2Dカスタマーセンターへご連絡ください。",
    tradingAgencyPayerQuestion: "中国の有料貿易代行サービス料の請求先は？",
    tradingAgencyHint: () => (
      <>
        中国の有料貿易代行サービスとは？
        <br />
        中国のSHIPPER（輸出者）が貿易ライセンスを保有していない場合、または、SHIPPER（輸出者）が貿易ライセンスを保有する貿易代行会社をご用意できない場合、当社が、有料のオプションサービスにて、中国から日本への輸出のために、貿易代行のサービス（1件あたりUSD60.00）をご提供いたします。
        <br />
        わからないを選択した場合、
        <span>
          中国のSHIPPER（輸出者）に確認してください。それでも、ご不明な場合には、弊社へご相談ください
        </span>
        。
        <br />
        弊社が<span>SHIPPER（輸出者）</span>
        へ確認後、予約の内容を修正いたします。
        <br />
        貿易代行サービス料金のご請求に関して：　一般的には貿易条件がEXWの場合、
        <span>CNEE（輸入者）</span>
        に請求いたします。
        <br />
      </>
    ),
    missingResolvedBookingInquiry:
      "予約をご入力の上、ルート選択にお進みください。",
    fillFromBeginning: "最初から入力する",
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    rowGap: "1rem",
    flexShrink: 1,
    flex: 1,
    overflow: "hidden",
  },
  bookingInquiryConfirmContainer: {
    padding: 8,
    backgroundColor: theme.palette.grey[100],
  },
  adminContainer: {
    ...common.flexColumn,
    flex: 1,
    padding: "1.5rem 2rem",
  },
  form: {
    ...common.flexColumn,
    rowGap: "1rem",
  },
  confirmForm: {
    ...common.flexColumn,
    rowGap: "1rem",
    backgroundColor: theme.palette.background.paper,
  },
  choicesContainer: {
    ...common.textGray700,
    padding: "1rem",
  },
  tradingAgencyContainer: {
    ...common.textGray700,
    display: "grid",
    padding: "1rem",
    gridTemplateColumns: "1fr 1fr",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      rowGap: "1rem",
    },
  },
  additionalFilesContainer: {
    ...common.textGray700,
    ...common.middleText,
    padding: "30px 1rem",
  },
  navigationButtonContainer: {
    ...common.flexInline,
    columnGap: 40,
  },
  formConfirmationSubcontainer: {
    padding: 20,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      padding: "1rem",
    },
  },
  checkboxRoot: {
    marginTop: -5,
    marginBottom: -5,
    marginRight: -5,
  },
  label: {
    ...common.flexInline,
    ...common.justifyBetween,
    width: 150,
  },
  checkbox: {
    height: 15,
    width: 15,
    "& svg": {
      height: 7,
      width: 8,
    },
  },
  radio: {
    height: 15,
    width: 15,
    marginLeft: -15,
  },
  checkboxLabel: {
    color: `${theme.palette.grey[700]} !important`,
    "& a": {
      ...common.link,
      ...common.baseButton,
    },
  },
  inputTip: {
    ...common.textGray700,
    ...common.smallerText,
    margin: "0.25rem 0",
    paddingLeft: 170,
    "& a": {
      ...common.caution,
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: 0,
    },
  },
  dashedDivider: {
    borderBottom: `1px dashed ${theme.palette.grey[500]}`,
  },
  tradingAgencyHintContainer: {
    ...common.flexInline,
    ...common.smallerText,
    ...common.textGray700Important,
    columnGap: "5px",
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
    },
    "& span": {
      ...common.fontWeight600,
    },
  },
  routePlanForm: {
    ...common.flexInline,
    ...common.itemsStart,
    columnGap: 40,
    overflow: "hidden",
    flexShrink: 1,
    flex: 1,
  },
}));

const searchType = t.type({
  stepName: h.omittable(h.string()),
  formStepIndex: h.omittable(h.number().cast()),
});

const SelectRoutePlan = React.memo(({ onGoBack }: { onGoBack: () => void }) => {
  const { resolvePostBookingInquiryResult, ownerId } =
    useBookingCreateContext();

  const bookingInquiry = resolvePostBookingInquiryResult?.postBookingInquiry!;

  const routePlanSimulator = useRoutePlanSimulator(
    {
      zipcode: bookingInquiry.receiver?.zip_code!,
      cargos: bookingInquiry.cargos!,
      move_in_date: bookingInquiry.move_in_date!,
      pickup_date: bookingInquiry.pickup_date!,
      trade_condition: bookingInquiry.trade_condition!,
      trading_agency_payer: bookingInquiry.trading_agency_payer!,
      use_trading_agency: bookingInquiry.use_trading_agency!,
      owner_id: ownerId,
      city_cd: undefined,
      truck_type: undefined,
    },
    {
      enabled: !!bookingInquiry,
      refetchOnWindowFocus: false,
    },
  );

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <QueryContainer
      queries={[routePlanSimulator]}
      className={classes.flex1}
      renderError={() => {
        if (routePlanSimulator.error instanceof Error) {
          return (
            <div style={{ flex: 1 }} className={classes.flexColumn}>
              <Alert severity="error" open gutter>
                {routePlanSimulator.error instanceof FormError
                  ? routePlanSimulator.error.getDisplayMessage()
                  : routePlanSimulator.error.message}
              </Alert>
              <div className={classes.navigationButtonContainer}>
                <Button
                  text={strings.edit}
                  color="primary"
                  size="big"
                  style={{ flex: 1 }}
                  type="button"
                  onClick={onGoBack}
                />
              </div>
            </div>
          );
        }
      }}
    >
      {() => (
        <>
          <RoutePlanFilter
            name=""
            routePlanSimulators={routePlanSimulator.data!}
            onGoBack={onGoBack}
          />
          <RoutePlanList
            name=""
            routePlanSimulators={routePlanSimulator.data!}
          />
        </>
      )}
    </QueryContainer>
  );
});

const FormBookingInquiryDisplay: React.FC<{
  onOpenCautiousDialog: () => void;
}> = React.memo(({ onOpenCautiousDialog }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isMobile = useIsMobile();

  const { isDashboard, resolvePostBookingInquiryResult } =
    useBookingCreateContext();

  const isCautionCheckedError = useErrorMessage("is_caution_checked");

  return (
    <>
      <div
        className={
          isDashboard ? classes.bookingInquiryConfirmContainer : undefined
        }
      >
        <BookingInquiryDisplay
          bookingInquiry={resolvePostBookingInquiryResult!.postBookingInquiry}
          cargosDisplayVariant="inline"
        />
      </div>

      <div style={{ padding: "18px 0" }}>
        <CheckboxInput
          name="is_caution_checked"
          value="Y"
          classes={{ checkbox: classes.checkbox, radio: classes.checkbox }}
          label={
            <label
              htmlFor="is_caution_checked"
              className={clsx(
                classes.normalText,
                classes.baseButton,
                !isCautionCheckedError && classes.checkboxLabel,
                isCautionCheckedError && classes.textErrorImportant,
              )}
            >
              {strings.confirmedCautions(() => {
                if (!isMobile) {
                  onOpenCautiousDialog();
                } else {
                  window.open(
                    url("/utilities/BookingInquiry/MobileCautions"),
                    "_blank",
                  );
                }
              })}
            </label>
          }
        />
      </div>
    </>
  );
});

const TradeAgencyInput: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [field] = useField<UseTradingAgency>("use_trading_agency");

  return (
    <div className={classes.tradingAgencyContainer}>
      <div>
        <div className={classes.middleText}>
          {strings.useTradingAgencyQuestion}
          <span className={classes.textError}>*</span>
          <br />
          {strings.useTradingAgencyHint}
        </div>

        <VerticalSpace height={10} />

        <RadioGroupInput
          noLabel
          label=""
          name="use_trading_agency"
          iconSize="small"
          formControlLabelClasses={{
            label: classes.middleTextImportant,
          }}
          options={tradingAgencyOptions}
        />

        <VerticalSpace height={13} />

        <div
          className={clsx(
            classes.middleText,
            field.value !== "USED" && classes.textGray400,
          )}
        >
          {strings.tradingAgencyPayerQuestion}
          <span className={classes.textError}>*</span>
        </div>

        <VerticalSpace height={10} />

        <RadioGroupInput
          noLabel
          label=""
          name="trading_agency_payer"
          iconSize="small"
          disabled={field.value !== "USED"}
          formControlLabelClasses={{
            label: classes.middleTextImportant,
          }}
          options={[
            {
              value: "cn",
              label: countryStrings.cn,
            },
            {
              value: "jp",
              label: countryStrings.jp,
            },
          ]}
        />
      </div>

      <div className={classes.tradingAgencyHintContainer}>
        <div>
          <img
            src={infoSvg}
            style={{ height: 14, width: 14, position: "relative", top: 2 }}
            alt="Info"
          />
        </div>
        <div>{strings.tradingAgencyHint()}</div>
      </div>
    </div>
  );
};

const BookingCreateFormContent = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    isDashboard,
    ownerId,
    resolvePostBookingInquiryResult,
    setResolvePostBookingInquiryResult,
    onChangeCreating,
    initialValues,
    setInitialValues,
    initialFormIndexStep,
  } = useBookingCreateContext();

  const {
    search: { stepName = steps[0].name, formStepIndex = initialFormIndexStep },
    updateSearch,
  } = useSearch(searchType);

  const doTemporarilySaveRef = useRef(false);

  const postBookingInquiryDraft = usePostBookingInquiryDraft();

  const validate = useCallback(
    (values: any) =>
      doTemporarilySaveRef.current
        ? () => ({})
        : makeValidateBookingInquiryForm(stepName ?? steps[0].name)(values),
    [stepName],
  );

  useEffect(() => {
    // Avoid visiting Confirmation at the beginning
    if (!import.meta.env.DEV && stepName !== "InquiryForm") {
      updateSearch({ stepName: "InquiryForm" });
    }
  }, []);

  const history = useHistory();

  const resolvePostBookingInquiry = useResolvePostBookingInquiry();

  const bookingInquiryCreate = useBookingInquiryCreate();

  const [cautiousDialogOpen, setCautiousDialogOpen] = useState(false);
  const [fixErrorOpen, setFixErrorOpen] = useState(false);

  const formikRef = useRef<FormikProps<BookingInquiryForm> | null>(null);

  const scrollToTop = useCallback(() => {
    requestIdleCallback(() => {
      document
        .querySelector(`*[role="top-anchor"]`)
        ?.scrollIntoView({ block: "start" });
    });
  }, []);

  const onGoToFirstPage = useCallback(() => {
    updateSearch({ stepName: steps[0].name });
  }, [stepName]);

  const onClickNext = () => {
    updateSearch({
      formStepIndex: formStepIndex! + 1,
    });
  };

  const renderTradeConditions = () => {
    return (
      <div className={classes.choicesContainer}>
        <div className={clsx(classes.middleText, classes.textGray800)}>
          {strings.tradeConditionsPrompt}
          <span className={classes.textError}>*</span>
        </div>

        <VerticalSpace height={20} />

        <TradeConditionSelector
          name="trade_condition"
          hasPickupsFieldName="has_pickups"
        />

        <VerticalSpace height={20} />
      </div>
    );
  };

  const renderFormCommon = () => (
    <>
      <FormAlert mutation={resolvePostBookingInquiry} />
      <SubmittingOverlay />
      <LogForm name="booking-inquiry" disabled={import.meta.env.PROD} />
      <PromptLeave
        title={formStrings.confirm}
        message={formStrings.promptLeaving}
        shouldBlockOnLocation={(location) =>
          location.pathname !== url("/utilities/BookingInquiry/InquiryForm") &&
          location.pathname !== url("/admin/booking/BookingCreate")
        }
      />
    </>
  );

  const renderResolvedBookingInquiryNotFound = () => (
    <>
      <div
        className={clsx(classes.textError, classes.textAlignCenter)}
        style={{ marginBottom: "1rem" }}
      >
        {strings.missingResolvedBookingInquiry}
      </div>
      <div className={classes.navigationButtonContainer}>
        <Button
          text={strings.fillFromBeginning}
          color="outlined"
          size="big"
          style={{ flex: 1 }}
          type="button"
          onClick={() => {
            updateSearch({ stepName: steps[0].name });
            scrollToTop();
          }}
        />
      </div>
    </>
  );

  return (
    <>
      <CautionsDialog
        open={cautiousDialogOpen}
        onClose={() => setCautiousDialogOpen(false)}
      />

      <div className={classes.container}>
        <div style={{ position: "relative", top: -200 }} role="top-anchor" />

        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: 4,
            padding: "16px 20px",
            color: "#664d03",
            fontSize: 14,
            lineHeight: 1.8,
          }}
        >
          <strong>【重要】システム移行のお知らせ</strong>
          <br />
          本システムは新システムに移行中です。
          新システムへのアカウントをお持ちの場合、
          <a
            href="https://online.fcdf.jp"
            target="_blank"
            rel="noreferrer"
            style={{ color: "#0d6efd", textDecoration: "underline" }}
          >
            新システム
          </a>
          よりご予約いただくことをお願いします。
          <br />
          新システムのアカウントが無い方は
          <a
            href="https://d2dasia.com/about-d2d/"
            target="_blank"
            rel="noreferrer"
            style={{ color: "#0d6efd", textDecoration: "underline" }}
          >
            こちらのフォーム
          </a>
          または
          <a href="mailto:cs@fcstandard.com">顧客窓口</a>
          よりご連絡ください。
        </div>

        <Alert
          open={fixErrorOpen}
          onClose={() => setFixErrorOpen(false)}
          severity="error"
          scrollOnOpen={false}
        >
          {strings.fixError}
        </Alert>

        <FormAlert mutation={postBookingInquiryDraft} showSuccess />

        <FormAlert mutation={bookingInquiryCreate} />

        {isDashboard ? (
          <SectionTitle>{strings.dashboardTitle}</SectionTitle>
        ) : (
          <div className={clsx(classes.pageTitleText, classes.textPrimary)}>
            {strings.title}
          </div>
        )}

        <Stepper
          baseUrl="/utilities/BookingInquiry/"
          steps={steps}
          value={stepName!}
        />

        <Formik<BookingInquiryForm>
          innerRef={formikRef}
          initialValues={initialValues}
          validate={validate}
          validateOnMount
          onSubmit={async (_values, { setFieldError, setFieldValue }) => {
            setFixErrorOpen(false);

            postBookingInquiryDraft.reset();

            try {
              if (doTemporarilySaveRef.current) {
                await postBookingInquiryDraft.mutateAsync({
                  form_step_index: formStepIndex || 1,
                  data: _values,
                  step: stepName || "InquiryForm",
                });
                setInitialValues(_values);
              } else {
                const values = unwrap(bookingInquiryForm, _values);

                if (values.has_pickups) {
                  let i = 0;
                  let hasError = false;
                  for (const cargo of values.cargos2) {
                    if (!cargo.pickup_address_index) {
                      setFieldError(
                        joinKeyPath("cargos2", i, "pickup_address_index"),
                        bookingInquiryStrings.atLeastOnePickupAddress,
                      );
                      hasError = true;
                    }
                    i++;
                  }
                  if (hasError) {
                    setFixErrorOpen(true);
                    scrollToTop();
                    return;
                  }
                }

                if (stepName === "Confirmation") {
                  if (resolvePostBookingInquiry.data == null) {
                    throw new Error("Unexpected unresolved form values");
                  }

                  const resolved =
                    resolvePostBookingInquiryResult?.postBookingInquiry!;

                  const bookingInquiryCreateResponse =
                    await bookingInquiryCreate.mutateAsync(resolved);

                  const d2dNo = bookingInquiryCreateResponse.d2d_no;
                  const id = bookingInquiryCreateResponse.id;

                  const completeUrl = isDashboard
                    ? `${url("/admin/booking/BookingCreateComplete")}?d2dNo=${d2dNo}&id=${id}`
                    : `${url("/utilities/BookingInquiry/Complete")}?d2dNo=${d2dNo}&id=${id}`;
                  history.replace(completeUrl);
                  scrollToTop();
                  return;
                }

                if (
                  stepName === "InquiryForm" ||
                  stepName === "SelectRoutePlan"
                ) {
                  const resolved = await resolvePostBookingInquiry.mutateAsync({
                    owner: ownerId,
                    form: values,
                    isDashboard,
                  });

                  console.info("resolvePostBookingInquiryResult", resolved);

                  if (isLeft(resolved)) {
                    const errors = resolved.left;
                    if (errors.shipper) {
                      setFieldError("shipper", errors.shipper);
                    }
                    if (errors.receiver) {
                      setFieldError("receiver", errors.receiver);
                    }
                    if (errors.cargos_port_name) {
                      setFieldError(
                        "cargos_port_name",
                        errors.cargos_port_name,
                      );
                    }
                    if (errors.move_in_date) {
                      setFieldError("move_in_date", errors.move_in_date);
                    }
                    if (errors.pickup_date) {
                      setFieldError("pickup_date", errors.pickup_date);
                    }
                    if (errors.invalidChinaZipcodes) {
                      setFieldError(
                        `cargos_pickup_addresses`,
                        bookingInquiryStrings.invalidChinaZipcodes(
                          errors.invalidChinaZipcodes,
                        ),
                      );
                    }

                    if (
                      Object.keys(errors).length === 1 &&
                      (errors.move_in_date || errors.pickup_date) &&
                      stepName === "SelectRoutePlan"
                    ) {
                      // Do nothing because user can fix that on the current page
                    } else {
                      updateSearch({ stepName: "InquiryForm" });
                    }

                    setFixErrorOpen(true);
                    scrollToTop();
                  } else {
                    setFieldValue(
                      "receiving_warehouse_cities",
                      resolved.right.postBookingInquiry.cargos.cargos_by_port?.map(
                        (c) => c.port_name,
                      ) ?? receivingCityOptions.map((o) => o.name),
                    );

                    setResolvePostBookingInquiryResult(resolved.right);
                    setFixErrorOpen(false);

                    updateSearch({
                      stepName:
                        steps[steps.findIndex((s) => s.name === stepName) + 1]
                          .name,
                    }); // Go to the next step

                    scrollToTop();
                  }
                }
              }
            } finally {
              doTemporarilySaveRef.current = false;
            }
          }}
          enableReinitialize
        >
          {({ values, isValid }) => {
            switch (stepName) {
              case steps[0].name:
                return (
                  <Form className={classes.form}>
                    {renderFormCommon()}
                    <FormStepSection
                      id={1}
                      title={strings.shipperInfoTitle}
                      currentStep={formStepIndex!}
                      onClickNext={onClickNext}
                      splitHalf
                      splitRightTitle={strings.consigneeInfoTitle}
                      splitRightChildren={<ConsigneeInfoInput />}
                      required
                    >
                      <ShipperInfoInput />
                    </FormStepSection>

                    <FormStepSection
                      id={2}
                      title={strings.receiverInfoTitle}
                      currentStep={formStepIndex!}
                      onClickNext={onClickNext}
                      splitHalf
                      splitRightTitle={strings.deliveryDateTitle}
                      splitRightChildren={<PreferredArrivalDateInput />}
                      required
                      rightRequired={false}
                    >
                      <ReceiverInfoInput />
                    </FormStepSection>

                    <FormStepSection
                      id={3}
                      title={
                        formStepIndex! >= 3
                          ? strings.pickupOptions
                          : strings.cargoInfoTitle
                      }
                      currentStep={formStepIndex!}
                      onClickNext={onClickNext}
                      splitRightTitle={
                        unwrap(
                          bookingInquiryForm.props.has_pickups,
                          values.has_pickups,
                        )
                          ? strings.cargoPickupTitle
                          : strings.cargoPortTitle
                      }
                      splitRightChildren={
                        <CargosInput name="" isDashboard={isDashboard} />
                      }
                      required
                    >
                      <PickupInfoInput />
                    </FormStepSection>

                    <FormStepSection
                      id={4}
                      title={
                        formStepIndex! >= 4
                          ? strings.tradeConditionInfoTitle
                          : strings.miscInfoTitle
                      }
                      currentStep={formStepIndex!}
                      onClickNext={onClickNext}
                      splitRightTitle={strings.customerNoSpecialNoteTitle}
                      splitRightChildren={<CustomerInfoInput />}
                      required
                      rightRequired={false}
                    >
                      {renderTradeConditions()}
                    </FormStepSection>

                    <FormStepSection
                      id={5}
                      title={
                        formStepIndex! >= 5
                          ? bookingInquiryStrings.tradingAgency
                          : `${bookingInquiryStrings.tradingAgency}、${bookingInquiryStrings.useRcep}`
                      }
                      currentStep={formStepIndex!}
                      onClickNext={onClickNext}
                      splitHalf
                      splitRightTitle={bookingInquiryStrings.useRcep}
                      splitRightChildren={<RcepUsageInput />}
                      required
                    >
                      <TradeAgencyInput />
                    </FormStepSection>

                    <FormStepSection
                      id={6}
                      title={bookingInquiryStrings.specialNote}
                      currentStep={formStepIndex!}
                      hasNext={false}
                      required
                    >
                      <BookingInquirySpecialNote />
                    </FormStepSection>

                    <div className={classes.navigationButtonContainer}>
                      <Button
                        text={
                          isDashboard ? formStrings.prev : formStrings.reset
                        }
                        color="outlined"
                        size="big"
                        style={{ flex: 1 }}
                        type="button"
                        onClick={() => {
                          if (isDashboard) {
                            onChangeCreating(false);
                          } else {
                            setInitialValues(bookingInquiryInitialValues);
                            updateSearch({ formStepIndex: 1 });
                            scrollToTop();
                          }
                        }}
                      />
                      {!isDashboard && (
                        <Button
                          text={formStrings.temporarilySave}
                          color="primary"
                          size="big"
                          style={{ flex: 1 }}
                          type="submit"
                          onClick={() => {
                            doTemporarilySaveRef.current = true;
                          }}
                        />
                      )}
                      <Button
                        text={strings.selectRoutePlan}
                        color="primary"
                        size="big"
                        style={{ flex: 1 }}
                        type="submit"
                        disabled={formStepIndex! !== 6}
                        onClick={() => {
                          doTemporarilySaveRef.current = false;
                          if (!isValid) {
                            setFixErrorOpen(true);
                            scrollToTop();
                          }
                        }}
                      />
                    </div>
                  </Form>
                );
              case steps[1].name:
                return (
                  <Form className={classes.form}>
                    {renderFormCommon()}
                    {resolvePostBookingInquiryResult && (
                      <div className={classes.routePlanForm}>
                        <SelectRoutePlan onGoBack={onGoToFirstPage} />
                      </div>
                    )}

                    {!resolvePostBookingInquiryResult &&
                      renderResolvedBookingInquiryNotFound()}
                  </Form>
                );
              case steps[2].name:
                return (
                  <Form>
                    {renderFormCommon()}

                    {resolvePostBookingInquiryResult && (
                      <>
                        <FormBookingInquiryDisplay
                          onOpenCautiousDialog={() =>
                            setCautiousDialogOpen(true)
                          }
                        />

                        <div className={classes.navigationButtonContainer}>
                          <Button
                            text={strings.edit}
                            color="outlined"
                            size="big"
                            style={{ flex: 1 }}
                            type="button"
                            onClick={() => {
                              updateSearch({ stepName: steps[1].name });
                              scrollToTop();
                            }}
                          />
                          <Button
                            text={formStrings.submit}
                            color="primary"
                            size="big"
                            style={{ flex: 1 }}
                            type="submit"
                            onClick={() => {
                              if (!isValid) {
                                setFixErrorOpen(true);
                                scrollToTop();
                              }
                            }}
                          />
                        </div>
                      </>
                    )}

                    {!resolvePostBookingInquiryResult &&
                      renderResolvedBookingInquiryNotFound()}
                  </Form>
                );
              default:
                throw new Error("Unknown step");
            }
          }}
        </Formik>
      </div>
    </>
  );
};

export interface BookingCreateFormProps {
  isDashboard?: boolean;
  ownerId: number;
  ownerCompanyId: number;
  ownerCompanyCountry: Country;
  onChangeCreating?: (v: boolean) => void;
}

export const BookingCreateForm: React.FC<BookingCreateFormProps> = (props) => {
  const {
    isDashboard = false,
    ownerId,
    ownerCompanyId,
    ownerCompanyCountry,
    onChangeCreating = () => {},
  } = props;

  const [resolvePostBookingInquiryResult, setResolvePostBookingInquiryResult] =
    useState<ResolvePostBookingInquiryResult>();

  const [initialValues, setInitialValues] = useState<BookingInquiryForm>();

  const user = useUser();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const bookingInquiryDraft = useBookingInquiryDraft(undefined, {
    enabled: !isDashboard,
    staleTime: Infinity, // Only fetch once on load
  });

  const initialValuesFromDraft = useMemo(
    () =>
      bookingInquiryDraft.data?.data
        ? mapBookingInquiryDraftToForm(bookingInquiryDraft.data)
        : bookingInquiryInitialValues,
    [bookingInquiryDraft.data],
  );

  useEffect(
    () => () => {
      bookingInquiryDraft.remove();
    },
    [],
  );

  return (
    <BookingCreateContext.Provider
      value={useShallowMemo(() => ({
        isDashboard,
        ownerId,
        ownerCompanyId,
        ownerCompanyCountry,
        resolvePostBookingInquiryResult,
        setResolvePostBookingInquiryResult,
        onChangeCreating,
        initialValues: initialValues ?? initialValuesFromDraft,
        setInitialValues,
        initialFormIndexStep: bookingInquiryDraft.data?.form_step_index ?? 1,
      }))}
    >
      {isDashboard ? (
        <AdminLayout
          title={strings.dashboardTitle}
          loading={user.isLoading}
          errors={[user.error]}
        >
          <div className={classes.adminContainer}>
            <BookingCreateFormContent />
          </div>
        </AdminLayout>
      ) : (
        <Layout
          title={strings.title}
          loading={user.isLoading || bookingInquiryDraft.isLoading}
          errors={[user.error, bookingInquiryDraft.error]}
          breadcrumbs={[
            "/utilities/Dashboard",
            "/utilities/BookingInquiry/InquiryForm",
          ]}
        >
          <Container>
            <BookingCreateFormContent />
          </Container>
        </Layout>
      )}
    </BookingCreateContext.Provider>
  );
};
